import api from '@/api'

import type { PortalApiResponse } from '@/types/apiResponses'
import type { Portal } from '@/types/portal'
import { useAppStore } from '@/stores/app'
import { useAppCookies } from '@/composables/useCookies'
import { idpLogout } from '@/api/idp'
import { apiClient, isEmbedded } from '@/api/client'
import { setSessionRevoked } from '@/helpers/embedHelper'
import { TokenHelper } from '@deltax/idp-api-client'
import type { BusinessProfile } from '@/types/businessProfile'
import type { User } from '@/types/user'

let sessionRecoveryInFlight: Promise<void> | null = null

class AppService {
  getPortalData = async (portalDomain: string): Promise<Portal> => {
    const portalData: PortalApiResponse = await api.get(
      'portals',
      { domain: portalDomain },
      { headers: { 'X-Bypass-Login': true } },
    )

    return portalData.data[0]
  }

  getCurrentUser = async (): Promise<User> => {
    const response = await api.get<{ data: { user: User; roleIds: number[] } }>('users/me')
    return response.data.user
  }

  getUserData = async (emailAddress: string) => {
    return api.get('users', { email: emailAddress })
  }

  getUserById = async (userId: string) => {
    return api.get('users', { ids: userId })
  }

  getBpUsers = async (businessProfileIds: number | null): Promise<User[]> => {
    const response = await api.get<{ data: User[] }>('users', { businessProfileIds })
    return response.data
  }

  getPortalBusinessProfiles = async (
    userId: number | undefined,
    portalId: number | undefined,
  ): Promise<BusinessProfile[]> => {
    if (!userId || !portalId) {
      return []
    }

    const response = await api.get<{ data: BusinessProfile[] }>(
      `portals/${portalId}/users/${userId}/businessProfiles`,
      { userId },
    )

    let businessProfiles = response.data
    businessProfiles = [
      ...new Map(businessProfiles.map((bp: BusinessProfile) => [bp.id, bp])).values(),
    ]
    return businessProfiles
  }


  saveTemplate = async (templateData: unknown) => {
    const appStore = useAppStore()
    return api.post(
      `businessProfiles/${appStore.selectedBusinessProfileId}/roimatic/creative-templates/`,
      templateData,
    )
  }

  logoutUser = async (): Promise<string> => {
    const appStore = useAppStore()
    appStore.setUser(null)
    appStore.setSelectedBusinessProfileId(null)
    this.removeUserTokens()
    return idpLogout(`${window.location.origin}/login`)
  }

  clearAuthData = async (_clearSessionInRedis: boolean = true) => {
    if (isEmbedded) {
      setSessionRevoked(true)
      window.onbeforeunload = null
      this.removeUserTokens()
      return
    }
    const endSessionUrl = await this.logoutUser()
    window.onbeforeunload = null
    window.location.href = endSessionUrl
  }

  recoverSession = async (redirectPath?: string) => {
    if (sessionRecoveryInFlight) {
      return sessionRecoveryInFlight
    }

    sessionRecoveryInFlight = (async () => {
      try {
        this.removeUserTokens()
        const currentPath =
          redirectPath || window.location.pathname + window.location.search + window.location.hash
        const targetRedirect =
          !currentPath || currentPath.startsWith('/login') || currentPath.startsWith('/callback')
            ? '/'
            : currentPath

        const { isEmbedded, authService } = await import('@/api/client')
        const { initiateAuthorize } = await import('@/api/idp')

        if (isEmbedded) {
          const { requestSessionTokenFromParent, getIsSessionRevoked } = await import('@/helpers/embedHelper')
          if (getIsSessionRevoked()) {
            return
          }
          sessionStorage.setItem('dx_internal_redirect', targetRedirect)
          const sessionToken = await requestSessionTokenFromParent()
          const authorizeUrl = await authService.createAuthorizationUrl(
            `${window.location.origin}/callback`,
            undefined,
            import.meta.env.VITE_RESOURCE,
            sessionToken
          )
          window.onbeforeunload = null
          window.location.href = authorizeUrl
        } else {
          await initiateAuthorize(targetRedirect, 'none')
        }
      } catch (error) {
        console.error('Session recovery failed, forcing logout', error)
        await this.clearAuthData(false)
      }
    })().finally(() => {
      sessionRecoveryInFlight = null
    })

    return sessionRecoveryInFlight
  }

  removeUserTokens = () => {
    const { removeCookies } = useAppCookies()

    TokenHelper.removeTokens(apiClient.storage, apiClient.accessTokenStorageKey)
    localStorage.removeItem('accessToken')
    removeCookies('user')
    removeCookies('accessToken')
    removeCookies('accessToken', '/', window.location.hostname)
    removeCookies('user', '/', window.location.hostname)
    removeCookies('accessToken', undefined, '.stunnr.co')
    removeCookies('user', undefined, '.stunnr.co')
  }
}

export default AppService
